import React, { useContext, useEffect, useState, useRef } from "react";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import Alarms from "./Alarms";
import DownUpLink from "./DownUpLink";
import Usage from "./Usage";
import DashboardInformation from "./DashboardInformation";
import {
  normalizeUnits,
  getAlarmsInfo,
  timeFormat,
  getSystemDate,
  getSubscriberGroupStatus,
  getSubscriberUsage,
  getLatency,
  getStatisticsPktEng,
  percentOver,
  getDbTCPOpt,
  getdbPps,
  formatLargeStrNum,
} from "./utils";
import Help from "./Help";

const Wrapper = styled.div`
  .context-menu {
    position: absolute;
    top: 0px;
    right: 20px;
    display: block;
    li {
      display: inline-block;
      list-style: none;
      i {
        font-size: 20px;
        color: #999;
        transition: all 0.5s; }
      }
    }
  }
`;

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
      </li>
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Refresh"
          onClick={() => actions.send("do-load")}
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};

const Dashboard = () => {
  const [alarms, setAlarms] = useState({});
  const [dynamics, setDynamics] = useState({});
  const [downUpLinkValues, setDownUpLinkValues] = useState({
    shapingDown: "0 %",
    shapingUp: "0 %",
    rateDown: "0 %",
    rateUp: "0 %",
    dbRateDw: "n/a Mbps",
    dbRateUp: "n/a Mbps",
    dbPpsDwValue: "n/a",
    dbPpsDwUnit: "Kpps",
    dbPpsUpValue: "n/a",
    dbPpsUpUnit: "Kpps",
  });
  const [usage, setUsage] = useState({
    opt: "0 %",
    flows: "0",
    groups: 0,
    subscribers: "0",
  });
  const actions = useContext(ActionsContext);
  const dynamicsInfoRef = useRef(null);

  const {
    TotalFlows,
    EpocTime,
    systemDate,
    TotalDnTxBytes,
    TotalUpTxBytes,
    ShpAllDnTxBytes,
    ShpAllUpTxBytes,
    RatePerSubsDnTxBytes,
    RatePerSubsUpTxBytes,
    TcpOptDnTxBytes,
    TcpOptUpTxBytes,
    TcpDnTxBytes,
    TcpUpTxBytes,
    TotalDnTxPkt,
    TotalUpTxPkt,
    activeGroups,
    activeSubs,
    latencyUp,
    latencyDown,
  } = dynamics;

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    const loadDynamics = () => {
      Promise.all([
        getAlarmsInfo(),
        getSystemDate(),
        getSubscriberUsage(),
        getStatisticsPktEng(),
        getLatency(),
        getSubscriberGroupStatus(),
      ]).then(
        ([
          responseAlarms,
          systemDate,
          activeSubs,
          statisticsPktEng,
          latencies,
          activeGroups,
        ]) => {
          if (isMounted) {
            setAlarms(responseAlarms);
            setDynamics({
              ...statisticsPktEng,
              systemDate,
              activeSubs,
              latencyUp: latencies.up,
              latencyDown: latencies.down,
              activeGroups,
            });
            timeoutId = setTimeout(loadDynamics, 2000);
          }
        }
      ).catch((error) => {
        console.error("Error fetching dynamic dashboard data:", error);
        if (isMounted) {
          timeoutId = setTimeout(loadDynamics, 5000);
        }
      });
    };

    loadDynamics();
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const totalDnTxBytesN = (TotalDnTxBytes * 8) / 1000000;
    const totalUpTxBytesN = (TotalUpTxBytes * 8) / 1000000;
    const ShpAllDnTxBytesN = (ShpAllDnTxBytes * 8) / 1000000;
    const ShpAllUpTxBytesN = (ShpAllUpTxBytes * 8) / 1000000;
    const RatePerSubsDnTxBytesN = (RatePerSubsDnTxBytes * 8) / 1000000;
    const RatePerSubsUpTxBytesN = (RatePerSubsUpTxBytes * 8) / 1000000;
    const TcpOptDnTxBytesN = (TcpOptDnTxBytes * 8) / 1000000;
    const TcpOptUpTxBytesN = (TcpOptUpTxBytes * 8) / 1000000;
    const TcpDnTxBytesN = (TcpDnTxBytes * 8) / 1000000;
    const TcpUpTxBytesN = (TcpUpTxBytes * 8) / 1000000;
    const TotalDnTxPktN = TotalDnTxPkt;
    const TotalUpTxPktN = TotalUpTxPkt;
    const activeGroupsN = activeGroups === undefined ? "unknown" : activeGroups;
    const activeSubsN = activeSubs === undefined ? "unknown" : activeSubs;
    const totalFlowsN =
      TotalFlows !== undefined
        ? formatLargeStrNum(TotalFlows.toString(), 0)
        : "unknown";

    const newUsage = {
      ...usage,
      flows: totalFlowsN,
      groups: activeGroupsN,
      subscribers: activeSubsN,
    };

    if (
      dynamicsInfoRef.current &&
      dynamicsInfoRef.current.EpocTime &&
      EpocTime - dynamicsInfoRef.current.EpocTime !== 0
    ) {
      const elapsed = (EpocTime - dynamicsInfoRef.current.EpocTime) / 1000;
      const {
        totalDnTxBytes: totalDnTxBytesCurrent,
        totalUpTxBytes: totalUpTxBytesCurrent,
        ShpAllDnTxBytes: ShpAllDnTxBytesCurrent,
        ShpAllUpTxBytes: ShpAllUpTxBytesCurrent,
        RatePerSubsDnTxBytes: RatePerSubsDnTxBytesCurrent,
        RatePerSubsUpTxBytes: RatePerSubsUpTxBytesCurrent,
        TcpOptDnTxBytes: TcpOptDnTxBytesCurrent,
        TcpOptUpTxBytes: TcpOptUpTxBytesCurrent,
        TcpDnTxBytes: TcpDnTxBytesCurrent,
        TcpUpTxBytes: TcpUpTxBytesCurrent,
        TotalDnTxPkt: TotalDnTxPktCurrent,
        TotalUpTxPkt: TotalUpTxPktCurrent,
      } = dynamicsInfoRef.current;
      const dbTCPOpt = getDbTCPOpt(
        TcpDnTxBytesN,
        TcpDnTxBytesCurrent,
        TcpUpTxBytesN,
        TcpUpTxBytesCurrent,
        TcpOptDnTxBytesN,
        TcpOptDnTxBytesCurrent,
        TcpOptUpTxBytesN,
        TcpOptUpTxBytesCurrent
      );
      newUsage.opt = dbTCPOpt;
      const { dbPpsValue: dbPpsDwValue, dbPpsUnit: dbPpsDwUnit } = getdbPps(
        TotalDnTxPktN,
        TotalDnTxPktCurrent,
        elapsed
      );
      const { dbPpsValue: dbPpsUpValue, dbPpsUnit: dbPpsUpUnit } = getdbPps(
        TotalUpTxPktN,
        TotalUpTxPktCurrent,
        elapsed
      );
      const dbDownTxShapProgress = percentOver(
        ShpAllDnTxBytesN - ShpAllDnTxBytesCurrent,
        totalDnTxBytesN - totalDnTxBytesCurrent
      );
      const dbUpTxShapProgress = percentOver(
        ShpAllUpTxBytesN - ShpAllUpTxBytesCurrent,
        totalUpTxBytesN - totalUpTxBytesCurrent
      );
      const dbDownTxRateProgress = percentOver(
        RatePerSubsDnTxBytesN - RatePerSubsDnTxBytesCurrent,
        totalDnTxBytesN - totalDnTxBytesCurrent
      );
      const dbUpTxRateProgress = percentOver(
        RatePerSubsUpTxBytesN - RatePerSubsUpTxBytesCurrent,
        totalUpTxBytesN - totalUpTxBytesCurrent
      );
      const dbRateDw = normalizeUnits(
        ((totalDnTxBytesN - totalDnTxBytesCurrent) / elapsed).toString(),
        "Mbps",
        3
      );
      const dbRateUp = normalizeUnits(
        ((totalUpTxBytesN - totalUpTxBytesCurrent) / elapsed).toString(),
        "Mbps",
        3
      );
      setDownUpLinkValues({
        shapingDown: dbDownTxShapProgress,
        shapingUp: dbUpTxShapProgress,
        rateDown: dbDownTxRateProgress,
        rateUp: dbUpTxRateProgress,
        dbRateDw,
        dbRateUp,
        dbPpsDwValue,
        dbPpsDwUnit,
        dbPpsUpValue,
        dbPpsUpUnit,
      });
    }

    setUsage(newUsage);
    dynamicsInfoRef.current = {
      EpocTime,
      totalDnTxBytes: totalDnTxBytesN,
      totalUpTxBytes: totalUpTxBytesN,
      ShpAllDnTxBytes: ShpAllDnTxBytesN,
      ShpAllUpTxBytes: ShpAllUpTxBytesN,
      RatePerSubsDnTxBytes: RatePerSubsDnTxBytesN,
      RatePerSubsUpTxBytes: RatePerSubsUpTxBytesN,
      TcpOptDnTxBytes: TcpOptDnTxBytesN,
      TcpOptUpTxBytes: TcpOptUpTxBytesN,
      TcpDnTxBytes: TcpDnTxBytesN,
      TcpUpTxBytes: TcpUpTxBytesN,
      TotalDnTxPkt: TotalDnTxPktN,
      TotalUpTxPkt: TotalUpTxPktN,
    };
  }, [
    TotalFlows,
    EpocTime,
    TotalDnTxBytes,
    TotalUpTxBytes,
    ShpAllDnTxBytes,
    ShpAllUpTxBytes,
    RatePerSubsDnTxBytes,
    RatePerSubsUpTxBytes,
    TcpOptDnTxBytes,
    TcpOptUpTxBytes,
    TcpDnTxBytes,
    TcpUpTxBytes,
    TotalDnTxPkt,
    TotalUpTxPkt,
    activeGroups,
    activeSubs,
  ]);

  const {
    shapingDown,
    shapingUp,
    rateDown,
    rateUp,
    dbRateDw,
    dbRateUp,
    dbPpsDwValue,
    dbPpsDwUnit,
    dbPpsUpValue,
    dbPpsUpUnit,
  } = downUpLinkValues;

  const isAlarmLicenseAvailable = alarms["license-available"]
    ? alarms["license-available"].status
    : undefined;
  const isAlarmLicenseExpiration = alarms["license-expiration"]
    ? alarms["license-expiration"].status
    : undefined;
  const isSpecAlarms =
    isAlarmLicenseAvailable !== "normal" ||
    isAlarmLicenseExpiration !== "normal";

  return (
    <Wrapper id="viewDashboard">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10">
            <h2>
              <big className="title">Dashboard</big>
            </h2>
          </div>
          <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2">
            <ContextMenu />
          </div>
        </div>
      </div>
      <div className="container-lg">
        <div className="row clearfix">
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <DashboardInformation systemDate={systemDate} />
          </div>
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
            <div className="row alarms-container">
              <Alarms data={alarms} />
            </div>
          </div>
        </div>
      </div>
      <div className="container-lg">
        <div className="row clearfix">
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
            <DownUpLink
              title="Downlink"
              shaping={isSpecAlarms ? "0 %" : shapingDown}
              shapingLink={{
                view: "viewThroughputPerPolicy",
                params: { directions: "downSpeed", policyType: "flow" },
              }}
              rate={isSpecAlarms ? "0 %" : rateDown}
              rateLink={{
                view: "viewThroughputPerPolicy",
                params: { directions: "downSpeed", policyType: "rate" },
              }}
              latency={latencyDown}
              latencyLink="viewLatencyStats"
              dbRate={dbRateDw}
              dbRateLink={{
                view: "viewSubscriberThroughput",
                params: { display: "downall" },
              }}
              dbPpsValue={dbPpsDwValue}
              dbPpsUnit={dbPpsDwUnit}
              dbPpsLink="viewBtaStatsTraffic"
            />
          </div>
          <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
            <DownUpLink
              title="Uplink"
              shaping={isSpecAlarms ? "0 %" : shapingUp}
              shapingLink={{
                view: "viewThroughputPerPolicy",
                params: { directions: "upSpeed", policyType: "flow" },
              }}
              rate={isSpecAlarms ? "0 %" : rateUp}
              rateLink={{
                view: "viewThroughputPerPolicy",
                params: { directions: "upSpeed", policyType: "rate" },
              }}
              latency={latencyUp}
              latencyLink="viewDPILatency"
              dbRate={dbRateUp}
              dbRateLink={{
                view: "viewSubscriberThroughput",
                params: { display: "upall" },
              }}
              dbPpsValue={dbPpsUpValue}
              dbPpsUnit={dbPpsUpUnit}
              dbPpsLink="viewBtaStatsTraffic"
            />
          </div>
        </div>
      </div>
      <div className="container-lg">
        <div className="row clearfix">
          <Usage
            opt={isSpecAlarms ? "0 %" : usage.opt}
            flows={usage.flows}
            groups={usage.groups}
            subscribers={usage.subscribers}
          />
        </div>
      </div>
      <Help />
    </Wrapper>
  );
};

export default Dashboard;
